import { useFormContext } from 'react-hook-form';
import { RadioField, NumberSelectField, TextField } from '@views/register/history/history-fields';
import type { TLocalize } from '@hooks/use-localize';
import type { IHistoryDiagnosedFormValues, IHistoryDiagnosedModel } from '@/types/history/history-diagnosed';

interface IProps {
    model: IHistoryDiagnosedModel;
    t: TLocalize;
}

const ScreeningsSection = ({ model, t }: IProps) => {
    const {
        watch,
        register,
    } = useFormContext<IHistoryDiagnosedFormValues>();

    const yn = model.yesNoUnknownOptions;
    const yes = String(model.yesValue);
    const selectYears = t('DashLabel_SelectYears', 'Select Years');

    return (
        <>
            <div className='section-heading'>Screenings</div>

            <RadioField
                name="hasHadPhysicalExam"
                label={t('DashLabel_HaveYouEverHadAPhysicalExam', 'Have you ever had a Physical Exam')}
                options={yn}
            />
            {watch('hasHadPhysicalExam') === yes && (
                <div className="sub-question">
                    <NumberSelectField
                        name="physicalExamYearsAgo"
                        label={t('DashLabel_HowManyYearsAgoWasYourPhysicalExam', 'How many years ago was your Physical Exam')}
                        max={10}
                        plusLast
                        placeholder={selectYears}
                    />
                </div>
            )}

            {!model.useReducedQuestions && !model.isMale && (
                <>
                    <RadioField
                        name="hasHadMammogram"
                        label={t('DashLabel_HaveYouEverHadAMammogram', 'Have you ever had a Mammogram')}
                        options={yn}
                    />
                    {watch('hasHadMammogram') === yes && (
                        <div className="sub-question">
                            <NumberSelectField
                                name="mammogramYearsAgo"
                                label={t('DashLabel_HowManyYearsAgoWasYourMamogram', 'How many years ago was your Mamogram')}
                                max={10}
                                plusLast
                                placeholder={selectYears}
                            />
                        </div>
                    )}
                    <RadioField
                        name="hasHadPapSmear"
                        label={t('DashLabel_HaveYouEverHadAPapSmear', 'Have you ever had a Pap Smear')}
                        options={yn}
                    />
                    {watch('hasHadPapSmear') === yes && (
                        <div className="sub-question">
                            <NumberSelectField
                                name="papSmearYearsAgo"
                                label={t('DashLabel_HowManyYearsAgoWasYourPapSmear', 'How many years ago was your Pap Smear')}
                                max={10}
                                plusLast
                                placeholder={selectYears}
                            />
                        </div>
                    )}
                </>
            )}

            {model.isMale && (
                <>
                    <RadioField
                        name="hasHadPsaTest"
                        label={t('DashLabel_HaveYouEverHadAPsaTest', 'Have you ever had a PSA Test')}
                        options={yn}
                    />
                    {watch('hasHadPsaTest') === yes && (
                        <div className="sub-question">
                            <NumberSelectField
                                name="psaTestYearsAgo"
                                label={t('DashLabel_HowManyYearsAgoWasYourPsaTest', 'How many years ago was your PSA Test')}
                                max={10}
                                plusLast
                                placeholder={selectYears}
                            />
                        </div>
                    )}
                </>
            )}

            {!model.useReducedQuestions && (
                <>
                    <RadioField
                        name="hasHadColonoscopy"
                        label={t('DashLabel_HaveYouEverHadAColonoscopy', 'Have you ever had a colonoscopy')}
                        options={yn}
                    />
                    {watch('hasHadColonoscopy') === yes && (
                        <div className="sub-question">
                            <NumberSelectField
                                name="colonoscopyYearsAgo"
                                label={t('DashLabel_HowManyYearsAgoWasYourColonoscopy', 'How many years ago was your colonoscopy')}
                                max={10}
                                placeholder={selectYears}
                            />
                        </div>
                    )}
                </>
            )}

            {/* Rabies question (BM-8568) - appears on all form types. */}
            <RadioField
                name="hasAnimalBiteExposure"
                label={t('DashLabel_HaveYouBeenBittenOrScratchedByAnimalInLast12Months', 'Have you been bitten or scratched by an animal in the last 12 months')}
                options={yn}
            />
            {watch('hasAnimalBiteExposure') === yes && (
                <div className="sub-question">
                    <TextField
                        name="animalBiteDescription"
                        label={t('DashLabel_WhatAnimalsWereInvolved', 'What animals were involved')}
                        maxLength={256}
                    />
                </div>
            )}

            {model.isLiverRegistration && (
                <>
                    <RadioField
                        name="hasActiveInfection"
                        label={t('DashLabel_DoYouHaveAnActiveInfectionOfAnyKind', 'Do you have an active infection of any kind')}
                        options={yn}
                    />
                    {watch('hasActiveInfection') === yes && (
                        <div className="sub-question">
                            <TextField
                                name="activeInfectionDescription"
                                label={t('DashLabel_PleaseDescribe', 'Please describe')}
                                maxLength={128}
                            />
                        </div>
                    )}
                </>
            )}

            <div className="form-group">
                <label className="control-label col-xs-12">
                    {t('DashLabel_IsThereAnythingInYourPastMedicalHistoryWeDidNotAsk', 'Is there anything in your past medical history we did not ask about or you would like to add')}?
                    <br />
                    <span className="label label-default">{t('Word_Optional', 'optional')}</span>
                </label>
                <div className="col-xs-12 col-sm-8">
                    <textarea className="form-control" {...register('additionalComments')} />
                </div>
            </div>
        </>
    );
};

export default ScreeningsSection;
